'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon, type IconSvgElement } from '@/components/ui/icon';
import { Award01IconSolid, Rocket01IconSolid } from '@/components/ui/icon/glyphs';
import { useMarketOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { Link } from '@/i18n/navigation';
import { cn } from '@/lib/ui/utils';
import type { LifecycleModalPayload, LifecycleVariant } from '@/stores/market-lifecycle-modal';
export interface LifecycleModalViewProps {
  payload: LifecycleModalPayload;
  /** Dismiss the modal before the selected CTA continues. */
  onClose: () => void;
}
type Tone = 'positive' | 'negative' | 'brand';
type PrimaryCta = 'trade' | 'claim' | 'browse';
interface VariantPresentation {
  tone: Tone;
  heroIcon: IconSvgElement;
  /** Show the winning-outcome label chip (resolution variants only). */
  showWinnerChip: boolean;
  primary: PrimaryCta;
  /** Show the secondary "Claim backer rewards" CTA. */
  showBackerCta: boolean;
}
const TONE_CLASSES: Record<Tone, {
  glow: string;
  iconWrap: string;
  icon: string;
}> = {
  positive: {
    glow: 'lifecycle-glow-positive',
    iconWrap: 'bg-feedback-success-surface',
    icon: 'text-feedback-success-content'
  },
  negative: {
    glow: 'lifecycle-glow-negative',
    iconWrap: 'bg-feedback-error-surface',
    icon: 'text-feedback-error-content'
  },
  brand: {
    glow: 'lifecycle-glow-brand',
    iconWrap: 'bg-background-brand',
    icon: 'text-foreground-brand'
  }
};
function toneForVariant(variant: LifecycleVariant): Tone {
  switch (variant) {
    case 'resolution-won-nonbacker':
    case 'resolution-won-backer':
      return 'positive';
    case 'resolution-lost-nonbacker':
    case 'resolution-lost-backer':
      return 'negative';
    default:
      return 'brand';
  }
}
function presentationFor(variant: LifecycleVariant, isBacker: boolean): VariantPresentation {
  const tone = toneForVariant(variant);
  switch (variant) {
    case 'graduation-generic':
    case 'graduation-backer':
    case 'graduation-creator':
      return {
        tone,
        heroIcon: Rocket01IconSolid,
        showWinnerChip: false,
        primary: 'trade',
        showBackerCta: false
      };
    case 'resolution-won-nonbacker':
      return {
        tone,
        heroIcon: Award01IconSolid,
        showWinnerChip: true,
        primary: 'claim',
        showBackerCta: false
      };
    case 'resolution-won-backer':
      return {
        tone,
        heroIcon: Award01IconSolid,
        showWinnerChip: true,
        primary: 'claim',
        showBackerCta: true
      };
    case 'resolution-lost-nonbacker':
      return {
        tone,
        heroIcon: Award01IconSolid,
        showWinnerChip: true,
        primary: 'browse',
        showBackerCta: false
      };
    case 'resolution-lost-backer':
      return {
        tone,
        heroIcon: Award01IconSolid,
        showWinnerChip: true,
        primary: 'browse',
        showBackerCta: true
      };
    case 'resolution-not-holder':
      // A pure backer with no outcome holdings still gets the backer claim path.
      return {
        tone,
        heroIcon: Award01IconSolid,
        showWinnerChip: true,
        primary: 'browse',
        showBackerCta: isBacker
      };
  }
}

/**
 * Presentational renderer for the lifecycle-modal variants. Branches on
 * `payload.variant` to pick a tone (corner glow), hero icon, copy, and the
 * primary / secondary CTAs. No animation is added here, so reduced-motion is
 * satisfied by construction (the only motion is SilkSheet's own overlay
 * transition, which already honors `prefers-reduced-motion`).
 *
 * NOTE (spec 112, FR-018): the hero is currently an icon-based placeholder.
 * When the final Figma art lands, swap the hero for the winning-outcome image
 * — render it via `OptimizedImage`/`OutcomeIndicator` so the NSFW overlay is
 * threaded (the `winningOutcome*` payload fields are carried for that purpose).
 */
export function LifecycleModalView(t0) {
  const $ = _c(107);
  const {
    payload,
    onClose
  } = t0;
  const t = useTranslations("market.lifecycle_modal");
  let t1;
  if ($[0] !== payload.isBacker || $[1] !== payload.variant) {
    t1 = presentationFor(payload.variant, payload.isBacker);
    $[0] = payload.isBacker;
    $[1] = payload.variant;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const p = t1;
  const tone = TONE_CLASSES[p.tone];
  const label = payload.winningOutcomeLabel ?? "";
  const winnerColor = useMarketOutcomeColor(payload.marketPubkey, payload.winnerOutcomeIndex ?? 0);
  let t2;
  bb0: {
    switch (payload.variant) {
      case "graduation-generic":
        {
          let t3;
          if ($[3] !== t) {
            t3 = t("graduationGeneric.headline");
            $[3] = t;
            $[4] = t3;
          } else {
            t3 = $[4];
          }
          let t4;
          if ($[5] !== t) {
            t4 = t("graduationGeneric.body");
            $[5] = t;
            $[6] = t4;
          } else {
            t4 = $[6];
          }
          let t5;
          if ($[7] !== t3 || $[8] !== t4) {
            t5 = {
              headline: t3,
              body: t4
            };
            $[7] = t3;
            $[8] = t4;
            $[9] = t5;
          } else {
            t5 = $[9];
          }
          t2 = t5;
          break bb0;
        }
      case "graduation-creator":
        {
          let t3;
          if ($[10] !== t) {
            t3 = t("graduationCreator.headline");
            $[10] = t;
            $[11] = t3;
          } else {
            t3 = $[11];
          }
          let t4;
          if ($[12] !== t) {
            t4 = t("graduationCreator.body");
            $[12] = t;
            $[13] = t4;
          } else {
            t4 = $[13];
          }
          let t5;
          if ($[14] !== t3 || $[15] !== t4) {
            t5 = {
              headline: t3,
              body: t4
            };
            $[14] = t3;
            $[15] = t4;
            $[16] = t5;
          } else {
            t5 = $[16];
          }
          t2 = t5;
          break bb0;
        }
      case "graduation-backer":
        {
          let t3;
          if ($[17] !== t) {
            t3 = t("graduationBacker.headline");
            $[17] = t;
            $[18] = t3;
          } else {
            t3 = $[18];
          }
          let t4;
          if ($[19] !== t) {
            t4 = t("graduationBacker.body");
            $[19] = t;
            $[20] = t4;
          } else {
            t4 = $[20];
          }
          let t5;
          if ($[21] !== t3 || $[22] !== t4) {
            t5 = {
              headline: t3,
              body: t4
            };
            $[21] = t3;
            $[22] = t4;
            $[23] = t5;
          } else {
            t5 = $[23];
          }
          t2 = t5;
          break bb0;
        }
      case "resolution-won-nonbacker":
        {
          let t3;
          if ($[24] !== t) {
            t3 = t("wonNonbacker.headline");
            $[24] = t;
            $[25] = t3;
          } else {
            t3 = $[25];
          }
          let t4;
          if ($[26] !== label || $[27] !== t) {
            t4 = t("wonNonbacker.body", {
              label
            });
            $[26] = label;
            $[27] = t;
            $[28] = t4;
          } else {
            t4 = $[28];
          }
          let t5;
          if ($[29] !== t3 || $[30] !== t4) {
            t5 = {
              headline: t3,
              body: t4
            };
            $[29] = t3;
            $[30] = t4;
            $[31] = t5;
          } else {
            t5 = $[31];
          }
          t2 = t5;
          break bb0;
        }
      case "resolution-won-backer":
        {
          let t3;
          if ($[32] !== t) {
            t3 = t("wonBacker.headline");
            $[32] = t;
            $[33] = t3;
          } else {
            t3 = $[33];
          }
          let t4;
          if ($[34] !== label || $[35] !== t) {
            t4 = t("wonBacker.body", {
              label
            });
            $[34] = label;
            $[35] = t;
            $[36] = t4;
          } else {
            t4 = $[36];
          }
          let t5;
          if ($[37] !== t3 || $[38] !== t4) {
            t5 = {
              headline: t3,
              body: t4
            };
            $[37] = t3;
            $[38] = t4;
            $[39] = t5;
          } else {
            t5 = $[39];
          }
          t2 = t5;
          break bb0;
        }
      case "resolution-lost-nonbacker":
        {
          let t3;
          if ($[40] !== t) {
            t3 = t("lostNonbacker.headline");
            $[40] = t;
            $[41] = t3;
          } else {
            t3 = $[41];
          }
          let t4;
          if ($[42] !== label || $[43] !== t) {
            t4 = t("lostNonbacker.body", {
              label
            });
            $[42] = label;
            $[43] = t;
            $[44] = t4;
          } else {
            t4 = $[44];
          }
          let t5;
          if ($[45] !== t3 || $[46] !== t4) {
            t5 = {
              headline: t3,
              body: t4
            };
            $[45] = t3;
            $[46] = t4;
            $[47] = t5;
          } else {
            t5 = $[47];
          }
          t2 = t5;
          break bb0;
        }
      case "resolution-lost-backer":
        {
          let t3;
          if ($[48] !== t) {
            t3 = t("lostBacker.headline");
            $[48] = t;
            $[49] = t3;
          } else {
            t3 = $[49];
          }
          let t4;
          if ($[50] !== label || $[51] !== t) {
            t4 = t("lostBacker.body", {
              label
            });
            $[50] = label;
            $[51] = t;
            $[52] = t4;
          } else {
            t4 = $[52];
          }
          let t5;
          if ($[53] !== t3 || $[54] !== t4) {
            t5 = {
              headline: t3,
              body: t4
            };
            $[53] = t3;
            $[54] = t4;
            $[55] = t5;
          } else {
            t5 = $[55];
          }
          t2 = t5;
          break bb0;
        }
      case "resolution-not-holder":
        {
          let t3;
          if ($[56] !== t) {
            t3 = t("notHolder.headline");
            $[56] = t;
            $[57] = t3;
          } else {
            t3 = $[57];
          }
          let t4;
          if ($[58] !== label || $[59] !== t) {
            t4 = t("notHolder.body", {
              label
            });
            $[58] = label;
            $[59] = t;
            $[60] = t4;
          } else {
            t4 = $[60];
          }
          let t5;
          if ($[61] !== t3 || $[62] !== t4) {
            t5 = {
              headline: t3,
              body: t4
            };
            $[61] = t3;
            $[62] = t4;
            $[63] = t5;
          } else {
            t5 = $[63];
          }
          t2 = t5;
          break bb0;
        }
    }
    t2 = undefined;
  }
  const copy = t2;
  const claim = payload.claim;
  const claimClaimed = claim?.claimed === true;
  const claimReady = claim?.finalized === true && claim?.claimable === true && !claimClaimed;
  let t3;
  if ($[64] !== claimClaimed || $[65] !== claimReady || $[66] !== t) {
    t3 = claimClaimed ? t("cta.claimed") : claimReady ? t("cta.claim") : t("cta.claimPending");
    $[64] = claimClaimed;
    $[65] = claimReady;
    $[66] = t;
    $[67] = t3;
  } else {
    t3 = $[67];
  }
  const claimLabel = t3;
  let t4;
  if ($[68] !== tone.iconWrap) {
    t4 = cn("flex size-16 items-center justify-center rounded-full", tone.iconWrap);
    $[68] = tone.iconWrap;
    $[69] = t4;
  } else {
    t4 = $[69];
  }
  const t5 = p.heroIcon;
  let t6;
  if ($[70] !== tone.icon) {
    t6 = cn("size-8", tone.icon);
    $[70] = tone.icon;
    $[71] = t6;
  } else {
    t6 = $[71];
  }
  let t7;
  if ($[72] !== p.heroIcon || $[73] !== t6) {
    t7 = <Icon icon={t5} className={t6} aria-hidden="true" />;
    $[72] = p.heroIcon;
    $[73] = t6;
    $[74] = t7;
  } else {
    t7 = $[74];
  }
  let t8;
  if ($[75] !== t4 || $[76] !== t7) {
    t8 = <div className={t4}>{t7}</div>;
    $[75] = t4;
    $[76] = t7;
    $[77] = t8;
  } else {
    t8 = $[77];
  }
  let t9;
  if ($[78] !== copy.headline) {
    t9 = <h2 className="text-heading font-bold text-foreground-primary">{copy.headline}</h2>;
    $[78] = copy.headline;
    $[79] = t9;
  } else {
    t9 = $[79];
  }
  let t10;
  if ($[80] !== copy.body) {
    t10 = <p className="text-body text-foreground-secondary">{copy.body}</p>;
    $[80] = copy.body;
    $[81] = t10;
  } else {
    t10 = $[81];
  }
  let t11;
  if ($[82] !== t10 || $[83] !== t9) {
    t11 = <div className="flex flex-col items-center gap-2">{t9}{t10}</div>;
    $[82] = t10;
    $[83] = t9;
    $[84] = t11;
  } else {
    t11 = $[84];
  }
  let t12;
  if ($[85] !== p.showWinnerChip || $[86] !== payload.winningOutcomeLabel || $[87] !== winnerColor) {
    t12 = p.showWinnerChip && payload.winningOutcomeLabel !== null ? <span className={cn("inline-flex w-fit items-center gap-1.5 rounded-pill px-2.5 py-1", winnerColor.outline)}><Icon icon={Award01IconSolid} className={cn("size-3.5", winnerColor.text)} aria-hidden="true" /><span className={cn("text-caption font-medium", winnerColor.text)}>{payload.winningOutcomeLabel}</span></span> : null;
    $[85] = p.showWinnerChip;
    $[86] = payload.winningOutcomeLabel;
    $[87] = winnerColor;
    $[88] = t12;
  } else {
    t12 = $[88];
  }
  let t13;
  if ($[89] !== claimLabel || $[90] !== claimReady || $[91] !== onClose || $[92] !== p.primary || $[93] !== t) {
    t13 = p.primary === "browse" ? <AnimatedButton asChild={true} variant="primary" size="lg" className="w-full"><Link href="/markets" onClick={onClose}>{t("cta.browse")}</Link></AnimatedButton> : p.primary === "trade" ? <AnimatedButton variant="primary" size="lg" className="w-full" onClick={onClose}>{t("cta.trade")}</AnimatedButton> : <AnimatedButton variant="primary" size="lg" className="w-full" onClick={onClose} disabled={!claimReady}>{claimLabel}</AnimatedButton>;
    $[89] = claimLabel;
    $[90] = claimReady;
    $[91] = onClose;
    $[92] = p.primary;
    $[93] = t;
    $[94] = t13;
  } else {
    t13 = $[94];
  }
  let t14;
  if ($[95] !== onClose || $[96] !== p.showBackerCta || $[97] !== t) {
    t14 = p.showBackerCta ? <AnimatedButton variant="secondary" size="lg" className="w-full" onClick={onClose}>{t("cta.claimBackerRewards")}</AnimatedButton> : null;
    $[95] = onClose;
    $[96] = p.showBackerCta;
    $[97] = t;
    $[98] = t14;
  } else {
    t14 = $[98];
  }
  let t15;
  if ($[99] !== t13 || $[100] !== t14) {
    t15 = <div className="flex w-full flex-col gap-2 pt-2">{t13}{t14}</div>;
    $[99] = t13;
    $[100] = t14;
    $[101] = t15;
  } else {
    t15 = $[101];
  }
  let t16;
  if ($[102] !== t11 || $[103] !== t12 || $[104] !== t15 || $[105] !== t8) {
    t16 = <div className="relative flex flex-col items-center gap-4 text-center">{t8}{t11}{t12}{t15}</div>;
    $[102] = t11;
    $[103] = t12;
    $[104] = t15;
    $[105] = t8;
    $[106] = t16;
  } else {
    t16 = $[106];
  }
  return t16;
}